import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import JobHero from './components/JobHero';
import JobContent from './components/JobContent';
import JobSidebar from './components/JobSidebar';
import Footer from '../home/components/Footer';
import { jobDetailData, similarJobs } from '../../mocks/jobDetail';
import ApplyModal from './components/ApplyModal';
import KakaoFloatingButton from '../../components/feature/KakaoFloatingButton';
import { useSEO } from '../../utils/seo';

export default function JobDetailPage() {
  const [isApplyOpen, setIsApplyOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const job = jobDetailData;
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: `${job.position} - ${job.companyName} | 온니잡 채용공고`,
    description: `${job.companyName}에서 ${job.position} 포지션을 모집합니다. ${job.salary} | ${job.location} | ${job.workType} | 온니잡에서 지금 바로 지원하세요.`,
    keywords: `${job.position}, ${job.companyName}, 유흥업 채용, 나이트라이프 구인, ${job.location} 채용`,
    canonical: `${siteUrl}/job-detail/1`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          '@id': `${siteUrl}/job-detail/1#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '홈', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: '채용정보', item: `${siteUrl}/jobs` },
            { '@type': 'ListItem', position: 3, name: job.position, item: `${siteUrl}/job-detail/1` }
          ]
        },
        {
          '@type': 'JobPosting',
          '@id': `${siteUrl}/job-detail/1#jobposting`,
          title: job.position,
          description: job.description,
          datePosted: job.postedDate,
          validThrough: job.deadline,
          employmentType: job.workType === '정규직' ? 'FULL_TIME' : 'PART_TIME',
          url: `${siteUrl}/job-detail/1`,
          hiringOrganization: {
            '@type': 'Organization',
            name: job.companyName,
            sameAs: siteUrl,
            logo: {
              '@type': 'ImageObject',
              url: job.companyLogo
            }
          },
          jobLocation: {
            '@type': 'Place',
            address: {
              '@type': 'PostalAddress',
              streetAddress: job.location,
              addressLocality: job.location.split(' ')[0],
              addressRegion: '서울',
              addressCountry: 'KR'
            }
          },
          baseSalary: {
            '@type': 'MonetaryAmount',
            currency: 'KRW',
            value: {
              '@type': 'QuantitativeValue',
              value: job.salary.includes('만원') ? parseInt(job.salary.replace(/[^0-9]/g, '')) * 10000 : 0,
              unitText: 'MONTH'
            }
          },
          workHours: job.workHours,
          qualifications: job.requirements.join(', '),
          jobBenefits: job.benefits.map((b: { title: string; desc: string }) => `${b.title}: ${b.desc}`).join(', '),
          experienceRequirements: job.experience,
          applicantLocationRequirements: {
            '@type': 'Country',
            name: '대한민국'
          },
          directApply: true,
          totalJobOpenings: 1
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-dark-deep">
      <JobHero
        badge={job.badge}
        position={job.position}
        companyName={job.companyName}
        location={job.location}
        experience={job.experience}
        salary={job.salary}
        workType={job.workType}
        workHours={job.workHours}
      />

      {/* Main Content */}
      <section className="relative py-12 bg-dark-deep">
        <div className="container mx-auto px-20">
          <div className="flex gap-10">
            <JobContent
              companyName={job.companyName}
              companyLogo={job.companyLogo}
              companyIntro={job.companyIntro}
              description={job.description}
              requirements={job.requirements}
              benefits={job.benefits}
            />
            <JobSidebar
              salary={job.salary}
              deadline={job.deadline}
              views={job.views}
              applicants={job.applicants}
              location={job.location}
              similarJobs={similarJobs}
              onApply={() => setIsApplyOpen(true)}
            />
          </div>
        </div>
      </section>

      <ApplyModal
        isOpen={isApplyOpen}
        onClose={() => setIsApplyOpen(false)}
        position={job.position}
        companyName={job.companyName}
      />

      <Footer />
      <KakaoFloatingButton />
    </div>
  );
}
